import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDocumentPdfLink, getReceiptPdfLink } from '../../api/client';

interface Props {
  kind: 'invoice' | 'receipt';
  documentType: number;
  documentNumber: number;
  documentDate?: string;
}

export default function DocumentLink({ kind, documentType, documentNumber, documentDate }: Props) {
  const { t } = useTranslation();
  const identity = `${kind}:${documentType}:${documentNumber}`;
  const [resolved, setResolved] = useState<{ identity: string; url: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const busy = useRef(false);
  const pendingTab = useRef<Window | null>(null);
  const label = `${t(`reconciliation.${kind}`)} #${documentNumber}`;
  const accessibleLabel = t('reconciliation.documentLinks.open', {
    document: documentDate ? `${label} · ${documentDate}` : label,
  });

  useEffect(() => {
    setError('');
    setLoading(false);
    busy.current = false;
    return () => {
      generation.current += 1;
      pendingTab.current?.close();
      pendingTab.current = null;
    };
  }, [identity]);

  const open = async () => {
    if (busy.current) return;
    busy.current = true;
    const epoch = generation.current;
    let tab: Window | null = null;
    setLoading(true);
    setError('');
    try {
      tab = window.open('about:blank', '_blank');
      if (tab) {
        tab.opener = null;
        tab.document.title = t('reconciliation.documentLinks.loading');
        const policy = tab.document.createElement('meta');
        policy.name = 'referrer';
        policy.content = 'no-referrer';
        tab.document.head.appendChild(policy);
      }
      pendingTab.current = tab;
      const response = kind === 'receipt'
        ? await getReceiptPdfLink(documentType, documentNumber)
        : await getDocumentPdfLink(documentType, documentNumber);
      const url = new URL(response.data.document_link);
      if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) {
        throw new Error('Invalid document link');
      }
      if (generation.current !== epoch) return;
      setResolved({ identity, url: url.href });
      if (tab && !tab.closed) {
        const link = tab.document.createElement('a');
        link.href = url.href;
        link.rel = 'noreferrer';
        link.referrerPolicy = 'no-referrer';
        tab.document.body.appendChild(link);
        link.click();
        link.remove();
      } else if (!tab) setError(t('reconciliation.documentLinks.popupBlocked'));
    } catch {
      if (generation.current !== epoch) return;
      tab?.close();
      console.error('[reconciliation] Document link lookup failed', { kind, documentType, documentNumber });
      setError(t('reconciliation.documentLinks.error'));
    } finally {
      if (generation.current === epoch) {
        pendingTab.current = null;
        busy.current = false;
        setLoading(false);
      }
    }
  };

  const url = resolved?.identity === identity ? resolved.url : undefined;
  const style = 'inline-flex items-center gap-1 font-semibold text-primary-700 underline underline-offset-2 focus-visible:outline focus-visible:outline-2 disabled:opacity-50';
  return (
    <span className="inline-block">
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer" aria-label={accessibleLabel} onClick={() => setError('')} className={style}>{label}</a>
      ) : (
        <button type="button" role="link" onClick={() => void open()} disabled={loading}
          aria-label={accessibleLabel} aria-busy={loading} className={style}>
          {label}{loading && <span className="text-xs">{t('reconciliation.documentLinks.loading')}</span>}
        </button>
      )}
      {error && <span role="alert" className="mt-1 block text-xs font-normal text-red-700">{error}</span>}
    </span>
  );
}
